/* eslint-disable import/no-cycle */
import * as _ from 'lodash';
import * as Sentry from "@sentry/react";

import {
  login,
  verifyToken,
  verifyTokenSilent,
  register,
  updateCompany,
  updateCompanyV2,
  updateLocations,
  verifyCodeEmail,
  sendEmailVerification,
  getTwoFactor,
} from '../utils/userAuth';
import { getLocationsAction } from './locationsDuck';
import { setFilterTypeAction } from './filtersDuck';
import { getChannelsAction } from './channelsDuck';
import { getSpacesAction } from './spacesDuck';
import { getPaymentCustomerAction } from './paymentDuck';
import { getTokenFirebase } from '../utils/FCMInit';
import CONSTANTS from '../const/constants';
import PlanStatus from '../const/planStatus';

// Helper function to get Firebase token with timeout to prevent blocking login flow
const getFirebaseTokenWithTimeout = async (timeoutMs = 5000) => {
  try {
    console.log('Attempting to get Firebase token for push notifications');

    // Create a timeout promise that rejects after specified time
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Firebase token request timeout')), timeoutMs);
    });

    // Race between getting the token and the timeout
    const firebaseToken = await Promise.race([
      getTokenFirebase(),
      timeoutPromise
    ]);

    console.log('Firebase token obtained successfully');
    return firebaseToken;
  } catch (error) {
    console.log('Firebase token retrieval failed (authentication will continue):', error.message);
    // Return null - the server can handle null Firebase tokens
    return null;
  }
};

const USER_INITIAL_STATE = {
  loginLoading: false,
  data: {},
  loginError: null,
  registerLoading: false,
  registerError: null,
  validatingToken: false,
  loadingData: false,
  loadingDataError: false,
};

// constants
const LOGIN = 'LOGIN';
const LOGIN_ERROR = 'LOGIN_ERROR';
const SET_USER = 'SET_USER';
const CLEAR_DATA = 'CLEAR_DATA';
const REGISTER = 'REGISTER';
const REGISTER_ERROR = 'REGISTER_ERROR';
const VERIFY_TOKEN = 'VERIFY_TOKEN';
const REGISTER_END = 'REGISTER_END';
const SET_INSTANCE_COUNT = 'SET_INSTANCE_COUNT';
const SET_FIRST_FACTOR = 'SET_FIRST_FACTOR';
const SET_IS_AGENT_NAME_IN_MESSAGE_ACTIVE = 'SET_IS_AGENT_NAME_IN_MESSAGE_ACTIVE';
const SET_COMPANY_UPDATE = 'SET_COMPANY_UPDATE';
const MERGE_COMPANY = 'MERGE_COMPANY';
const LOAD_LOGIN_DATA = 'LOAD_LOGIN_DATA';
const LOAD_LOGIN_DATA_FINISHED = 'LOAD_LOGIN_DATA_FINISHED';
const LOAD_LOGIN_DATA_ERROR = 'LOAD_LOGIN_DATA_ERROR';

// reducer
export default function userReducer(state = USER_INITIAL_STATE, action) {
  const { type, payload } = action;
  switch (type) {
    case LOGIN:
      return { ...state, loginLoading: true, validatingToken: false };
    case SET_FIRST_FACTOR:
      return { ...state, loginLoading: false, validatingToken: false, data: payload };
    case LOGIN_ERROR:
      return {
        ...state,
        loginLoading: false,
        loginError: payload,
        validatingToken: false,
      };
    case LOAD_LOGIN_DATA:
      return { ...state, loadingData: true };
    case LOAD_LOGIN_DATA_FINISHED:
      return { ...state, loadingData: false };
    case LOAD_LOGIN_DATA_ERROR:
      return { ...state, loadingData: false, loadingDataError: true };
    case SET_USER:
      return {
        ...state,
        loginLoading: false,
        registerLoading: false,
        validatingToken: false,
        loginError: null,
        registerError: null,
        data: payload,
      };
    case CLEAR_DATA:
      return { ...USER_INITIAL_STATE };
    case REGISTER:
      return { ...state, registerLoading: true };
    case REGISTER_END:
      return { ...state, registerLoading: false };
    case REGISTER_ERROR:
      return {
        ...state,
        registerLoading: false,
        registerError: payload,
        validatingToken: false,
      };
    case VERIFY_TOKEN:
      return { ...state, validatingToken: true };
    case SET_INSTANCE_COUNT:
      return { ...state, data: payload };
    case SET_IS_AGENT_NAME_IN_MESSAGE_ACTIVE:
      return { ...state, data: payload };
    case SET_COMPANY_UPDATE:
        return { ...state, data: payload };
    case MERGE_COMPANY:
      return { ...state, data: payload };
    default:
      return state;
  }
}

// actions
export const loginUserAction = (userLoginData) => async (dispatch) => {
  try {
    await dispatch({ type: LOGIN, payload: null });

    // Get Firebase token with timeout to prevent blocking login flow
    const firebaseToken = await getFirebaseTokenWithTimeout();

    console.log("LOGIN USER DATA", userLoginData)
    const token = await login(userLoginData, firebaseToken);
    if (token) {
      const response = await verifyToken(token);
      if (response.id) {        
        const payload = { ...response };

        const newPermissions = {};
        response.rol.permissions.forEach((i) => {
          newPermissions[i.name] = true;
          return i;
        });
        if (newPermissions.readUsers) {
          await dispatch(setFilterTypeAction('all'));
        }
        // console.log('setting sentry params')
        Sentry.setUser({ email: payload?.email, id: payload?.id })

        payload.permissions = newPermissions;
        await dispatch({ type: SET_USER, payload });

        dispatch(getSpacesAction(response.spaces));

        if (response.company?.status === PlanStatus.AWAITING_FIRST_PAYMENT) {
          return CONSTANTS.FIRST_PAYMENT_ROUTE;
        }
        if (response.company?.status === PlanStatus.AWAITING_WA_VINCULATION) {
          return CONSTANTS.LINK_WHATSAPP_ROUTE;
        }
        if (response.company?.status === PlanStatus.PAUSED || response.company?.status === PlanStatus.INACTIVE) {
          return CONSTANTS.UPDATE_SUBSCRIPTION_ROUTE;
        }
      } else {
        await dispatch({ type: LOGIN_ERROR, payload: 'Error getting user info' });
      }
    } else {
      await dispatch({ type: LOGIN_ERROR, payload: 'null token' });
    }
    return '/chat';
  } catch (error) {
    console.error(error);
    await dispatch({ type: LOGIN_ERROR, payload: error.response ? error.response.data : 'Error' });
    throw error;
  }
};
export const getTwoFactorAction = (userLoginData) => async (dispatch) => {
  try {
    // Get Firebase token with timeout to prevent blocking two-factor flow
    const firebaseToken = await getFirebaseTokenWithTimeout();

    const {factor} = await getTwoFactor(userLoginData, firebaseToken);

    console.log("factor", factor);
    if (factor.status === "unverified") return "/qr-code";
    return "/two-factor";
  } catch (error) {
    console.error(error);
    await dispatch({
      type: LOGIN_ERROR,
      payload: error.response ? error.response.data : "Error",
    });
    throw error;
  }
};

export const setInstancesCountAction = (instanceCount) => async (dispatch, getState) => {
  const { data } = getState().user;
  const { company } = data;
  company.whatsappInstancesCount = instanceCount;
  data.company = _.cloneDeep(company);
  const newData = _.cloneDeep(data);
  dispatch({ type: SET_INSTANCE_COUNT, payload: newData });
};

export const setisAgentNameInMessageActiveAction = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.isAgentNameInMessageActive = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({ isAgentNameInMessageActive: isActive, id: company.id });
    await dispatch({ type: SET_IS_AGENT_NAME_IN_MESSAGE_ACTIVE, payload: newData });
  } catch (e) {
    console.log('setisAgentNameInMessageActiveAction ERROR => ', e);
  }
};
export const setcanUnarchiveConversations = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.canUnarchiveConversations = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      canUnarchiveConversations: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log('setisAgentNameInMessageActiveAction ERROR => ', e);
  }
};
export const canReturnInboxConversation = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.canReturnInboxConversation = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      canReturnInboxConversation: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log("setisAgentNameInMessageActiveAction ERROR => ", e);
  }
};

export const isBotAutoAssignActive = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.isBotAutoAssignActive = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      isBotAutoAssignActive: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log("setisBotAutoAssignActive ERROR => ", e);
  }
};
export const isBotAutoAssignActiveAfterDay = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.isBotAutoAssignActiveAfterDay = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      isBotAutoAssignActiveAfterDay: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
    return
  } catch (e) {
    console.log("setisBotAutoAssignActive ERROR => ", e);
  }
};
export const _setForceActiveAgentsReassignation = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.forceActiveAgentsReassignation = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      forceActiveAgentsReassignation: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
    return
  } catch (e) {
    console.log("_forceActiveAgentsReassignation ERROR => ", e);
  }
};
export const setAutomaticAssignment = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.automaticAssignment = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      automaticAssignment: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
    return
  } catch (e) {
    console.log("setAutomaticAssignment ERROR => ", e);
  }
};

export const isBotActiveAfterDay = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.isBotActiveAfterDay = isActive;
    if (!isActive) {
      company.isBotAutoAssignActiveAfterDay = false;
    }
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      isBotActiveAfterDay: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
    return
  } catch (e) {
    console.log("setisBotAutoAssignActive ERROR => ", e);
  }
};

export const showClientCloseOption = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.showClientCloseOption = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      showClientCloseOption: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log("showClientCloseOption ERROR => ", e);
  }
};

export const sentClientSurveyByDefault = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.sentClientSurveyByDefault = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      sentClientSurveyByDefault: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log("sentClientSurveyByDefault ERROR => ", e);
  }
};

export const archiveClosedClientByDefault = (isActive) => async (dispatch, getState) => {
  try {
    const { data } = getState().user;
    const { company } = data;
    company.archiveClosedClientByDefault = isActive;
    data.company = _.cloneDeep(company);
    const newData = _.cloneDeep(data);
    await updateCompanyV2({
      archiveClosedClientByDefault: isActive,
      id: company.id,
    });
    await dispatch({
      type: SET_COMPANY_UPDATE,
      payload: newData,
    });
  } catch (e) {
    console.log("archiveClosedClientByDefault ERROR => ", e);
  }
};

export const setShowActiveAgents = (isActive) => async (dispatch) => {
  await dispatch(
    updateCompanySetting("isShowActiveAgents", isActive, SET_COMPANY_UPDATE)
  );
};

export const setAddTagFromAds = (isActive) => async (dispatch) => {
  await dispatch(
    updateCompanySetting("includeAdsTags", isActive, SET_COMPANY_UPDATE)
  );
};

export const setShowGroupsAtReassign = (isActive) => async (dispatch) => {
  await dispatch(
    updateCompanySetting("isShowGroupsAtReassign", isActive, SET_COMPANY_UPDATE)
  );
};

export const updateCompanySetting =
  (settingName, isActive, actionType) => async (dispatch, getState) => {
    try {
      const { data } = getState().user;
      const { company } = data;
      company[settingName] = isActive;
      data.company = _.cloneDeep(company);
      const newData = _.cloneDeep(data);
      await updateCompanyV2({
        [settingName]: isActive,
        id: company.id,
      });
      await dispatch({
        type: actionType,
        payload: newData,
      });
    } catch (e) {
      console.log("updateCompanySetting ERROR => ", e);
    }
  };


export const registerUserAction = (userRegisterData) => async (dispatch) => {
  try {
    await dispatch({ type: REGISTER, payload: null });
    const token = await register(userRegisterData);
    if (token) {
      const response = await verifyToken(token);
      if (response.userId) {
        const payload = { ...response };
        const newPermissions = {};
        response.permissions.forEach((i) => (newPermissions[i.name] = i.action));
        payload.permissions = newPermissions;
        await dispatch({ type: SET_USER, payload });
      } else {
        await dispatch({ type: REGISTER_ERROR, payload: 'Error getting user info' });
      }
    } else {
      await dispatch({ type: REGISTER_ERROR, payload: 'null token' });
    }
    return true;
  } catch (error) {
    console.log(error);
    await dispatch({
      type: REGISTER_ERROR,
      payload: error.response ? error.response.data : 'Error',
    });
    return false;
  }
};

export const updateCompanyInfo = (companyUpdateInfo) => async () => {
  console.log('updateCompanyInfo 2222222222222222 ============');
  try {
    const companyInfo = await updateCompany(companyUpdateInfo);
    return companyInfo;
  } catch (error) {
    console.log('updateCompanyInfo error ============', error);

    throw error;
  }
};

export const updateLocationsAction = (locationsData, deletedLocations) => async (
  dispatch,
  getState,
) => {
  try {
    const companyId = getState().user.data.company.id;
    const updatedSuccessful = await updateLocations(locationsData, deletedLocations, companyId);
    if (updatedSuccessful) {
      dispatch(getLocationsAction());
      return true;
    }
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const verifyCodeAction = (code) => async (distpach) => {
  try {
    distpach({ type: REGISTER });
    const verificationToken = await verifyCodeEmail(code);
    distpach({ type: REGISTER_END });
    return verificationToken.data;
  } catch (error) {
    distpach({ type: REGISTER_END });
    throw error;
  }
};

export const sendEmailVerificationAction = (email, plan) => async (distpach) => {
  try {
    distpach({ type: REGISTER });
    const response = await sendEmailVerification(email, plan);
    distpach({ type: REGISTER_END });
    return response;
  } catch (error) {
    distpach({ type: REGISTER_END });
    throw error;
  }
};

export const onboardingRegisterAction = (token, data, code) => async () => {
  try {
    const formData = new FormData();
    formData.append('token', token);
    if (code) formData.append('code', code);
    _.mapKeys(data, (value, key) => {
      const newKey = key.replace('onboarding', '').toLowerCase();
      formData.append(newKey, value);
    });
    const registerData = await register(formData);
    return registerData;
  } catch (error) {
    throw error;
  }
};

export const verifyTokenAction = () => async (dispatch) => {
  try {
    await dispatch({ type: VERIFY_TOKEN, payload: null });
    const token = localStorage.getItem('token');
    if (token) {
      const response = await verifyToken(token);
      if (response.id) {
        const payload = { ...response };

        const newPermissions = {};
        response.rol.permissions.forEach((i) => {
          newPermissions[i.name] = true;
          return i;
        });
        if (newPermissions.readUsers) {
          dispatch(setFilterTypeAction('all'));
        }
        // console.log('setting sentry params')
        Sentry.setUser({ email: payload?.email, id: payload?.id })
        payload.permissions = newPermissions;
        dispatch({ type: SET_USER, payload });

        dispatch(getSpacesAction(response.spaces));

        dispatch(getChannelsAction(response.spaces));
      }
      dispatch({ type: LOGIN_ERROR, payload: 'Error getting user info' });
    } else dispatch({ type: LOGIN_ERROR, payload: 'Error getting user info' });
  } catch (error) {
    console.error(error);
  }
};

// Un snapshot de `company` está degradado cuando llegó durante un outage de
// billing (backend#244): existe la company pero sin datos de plan. La ausencia
// de `planName` ES la señal de degradación — el backend NO envía ningún flag.
export const isCompanyDegraded = (userData) =>
  Boolean(userData?.company) && !userData.company.planName;

// Single-flight con ownership: el interval de 60s y los listeners de
// focus/visibility pueden disparar el thunk casi al mismo tiempo. El lock guarda
// el TOKEN dueño del request en vuelo (no un booleano) para que, si un logout +
// login rápido se traslapa, el request viejo no libere el lock del nuevo (su
// token ya no coincide) ni el nuevo herede un lock huérfano.
// El lock nunca puede quedar pegado indefinidamente: verifyTokenSilent lleva
// timeout propio (15s), así que el `finally` siempre corre en tiempo acotado.
let refreshOwner = null;

// userMessages que el backend emite ante un token TERMINAL (inválido, expirado
// o usuario borrado) en /verifyToken. Ninguno es "UNAUTHORIZED": /verifyToken NO
// pasa por verifyAccessMiddleware, así que un token corrupto/expirado vuelve como
// 500 ERROR_DECRIPTYNG_TOKEN (verificado contra el backend). El interceptor de
// axios solo desloguea ante "UNAUTHORIZED", así que estos NO desloguean solos: si
// el retry los tratara como transitorios, poleaería para siempre con un token
// muerto. Se cortan explícitamente en el catch del thunk.
const TERMINAL_AUTH_MESSAGES = new Set([
  'ERROR_DECRIPTYNG_TOKEN',
  'ERROR_EXPIRED_TOKEN',
  'INVALID_TOKEN',
  'USER_NOT_FOUND',
  'NO_USER_WITH_TOKEN_FOUND',
  'UNAUTHORIZED',
]);

// El interceptor de axios (axios.js) rechaza con { message, internalCode,
// httpCode } donde `message` es el userMessage del backend.
const isTerminalAuthError = (error) => TERMINAL_AUTH_MESSAGES.has(error?.message);

// Re-request acotado para reparar un snapshot de company degradado sin recargar
// ni pisar estado derivado. Merge QUIRÚRGICO: reemplaza SOLO `data.company`,
// preservando `permissions`/`spaces`/`rol` (por eso NO usa SET_USER, que pisaría
// todo `data`). Devuelve true cuando ya está completa (o no había nada que
// reparar) para que el caller detenga el retry; false si sigue degradada.
export const refreshDegradedCompanyAction = () => async (dispatch, getState) => {
  if (refreshOwner) return false;
  if (!isCompanyDegraded(getState().user.data)) return true;

  const token = localStorage.getItem('token');
  if (!token) return false;

  refreshOwner = token;
  // Span de observabilidad: expone la latencia/resultado de la reparación a lo
  // largo del outage (guía del repo: envolver operaciones críticas en
  // Sentry.startSpan). El callback resuelve el outcome del thunk, que startSpan
  // devuelve tal cual.
  return Sentry.startSpan(
    { name: 'company.degraded.refresh', op: 'billing.repair' },
    async () => {
      try {
        // verifyTokenSilent reusa el cliente `API` pero NO desloguea por su
        // cuenta: un token terminal en /verifyToken vuelve como 500
        // ERROR_DECRIPTYNG_TOKEN (no "UNAUTHORIZED"), que el interceptor NO
        // intercepta. La distinción terminal-vs-transitorio la hace el catch.
        const response = await verifyTokenSilent(token);

        // Guard anti-sesión-cruzada: entre el request y su respuesta el usuario
        // pudo desloguearse/relogearse. Re-leemos el estado y el token actuales y
        // sólo mezclamos si (a) el token no cambió, (b) la respuesta es del mismo
        // usuario, y (c) el snapshot en Redux sigue degradado.
        const latest = getState().user.data;
        const sameSession = localStorage.getItem('token') === token;
        const sameUser = response?.id && response.id === latest?.id;

        if (
          sameSession &&
          sameUser &&
          isCompanyDegraded(latest) &&
          response.company?.planName
        ) {
          const payload = { ...latest, company: response.company };
          dispatch({ type: MERGE_COMPANY, payload });
          Sentry.addBreadcrumb({
            category: 'billing',
            message: 'company.degraded.repaired',
            level: 'info',
          });
          return true;
        }
        return false;
      } catch (error) {
        // Token terminal (inválido/expirado/usuario borrado): reintentar nunca lo
        // curaría. Deslogueamos igual que verifyToken en el mount (userAuth.js) y
        // que el path "UNAUTHORIZED" del interceptor. Sólo si seguimos en la MISMA
        // sesión (el token no cambió tras el await): si el usuario ya relogueó en
        // otra pestaña, NO pisamos la sesión nueva. El redirect desmonta la
        // layout, así que el interval del retry muere con la página.
        if (isTerminalAuthError(error) && localStorage.getItem('token') === token) {
          // captureMessage (no breadcrumb): en seguida hacemos redirect (recarga
          // completa), así que un breadcrumb nunca viajaría — solo se envían
          // adjuntos a un evento capturado después, y el runtime se resetea al
          // navegar. Un evento propio sí se intenta enviar (compite con el
          // redirect, pero es estrictamente mejor que un breadcrumb perdido).
          Sentry.captureMessage('company.degraded.retry.terminalAuth', {
            level: 'warning',
            tags: { component: 'userDuck', op: 'refreshDegradedCompany.terminalAuth' },
            extra: { userMessage: error?.message },
          });
          localStorage.clear();
          window.location.replace('/');
          return false;
        }
        // Error transitorio (red, timeout de 15s, 5xx no-auth): NO deslogueamos;
        // se reporta a Sentry y el siguiente tick del interval reintenta.
        console.error('refreshDegradedCompanyAction error', error);
        Sentry.captureException(error, { tags: { component: 'userDuck', op: 'refreshDegradedCompany' } });
        return false;
      } finally {
        // Sólo el dueño libera el lock: si otra sesión ya lo tomó, no lo pisamos.
        if (refreshOwner === token) refreshOwner = null;
      }
    },
  );
};

export const getCompanyStatusRoute = () => (dispatch, getState) => {
  const status = getState().user.data?.company?.status;
  if (status === PlanStatus.AWAITING_FIRST_PAYMENT) return CONSTANTS.FIRST_PAYMENT_ROUTE;
  if (status === PlanStatus.AWAITING_WA_VINCULATION) return CONSTANTS.LINK_WHATSAPP_ROUTE;
  if (status === PlanStatus.PAUSED || status === PlanStatus.INACTIVE) return CONSTANTS.UPDATE_SUBSCRIPTION_ROUTE;
  return '/chat';
};

export const getCompanyInfo = () => async (dispatch, getState) => {
  try {
    const companyId = getState()?.user?.data?.company?.id;
    if (companyId) {
      dispatch(getPaymentCustomerAction(getState().user.data.company.id));
    }
    // const { data } = getState().user;
    // const { company } = data;
    // const response = await updateCompany(company);
    // return response;
  } catch (error) {
    console.log(error);
    return error;
  }
}

export const getLoginDataAction = () => async (dispatch) => {
  try {
    dispatch({ type: LOAD_LOGIN_DATA });
    await dispatch(verifyTokenAction());
    // await dispatch(getCompanyInfo());
    dispatch({ type: LOAD_LOGIN_DATA_FINISHED });
  } catch (error) {
    dispatch({ type: LOAD_LOGIN_DATA_ERROR });
  }
};

export const clearDataAction = () => (dispatch) => {
  // Libera el lock del retry de company degradada al cerrar sesión. OJO: este
  // clearDataAction (userDuck) solo se dispatcha en el flujo de UpdateSubscription;
  // el logout del sidebar usa el clearDataAction de channelsDuck y NO pasa por
  // aquí. En ese camino el lock se libera igual en tiempo acotado: el `finally`
  // ownership-aware del thunk lo suelta al asentarse el request en vuelo, y
  // verifyTokenSilent tiene timeout de 15s, así que la nueva sesión salta a lo
  // más un intento.
  refreshOwner = null;
  try {
    if (typeof Sentry.setUser === 'function') {
      Sentry.setUser(null);
    }
  } catch (_e) {
  }
  return dispatch({ type: CLEAR_DATA, payload: null });
};
